import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface StcPriorityDto {
    id: number;
    companyNo: string;
    priorityNo: number;
    description1: string;
    updatedBy?: string;
    lastUpdate?: string;
    createdBy?: string;
    created?: string;
}

export interface CreateStcPriorityRequest {
    companyNo: string;
    priorityNo: number;
    description1: string;
}

export interface UpdateStcPriorityRequest {
    description1: string;
}

export interface BulkUpdateStcPriorityRequest {
    companyNo: string;
    priorityNo: number;
    description1: string;
}

export interface BulkDeleteResult {
    deletedCount: number;
    requestedCount: number;
    notFoundIds: number[];
}

class StcPriorityService {
    private baseUrl = `${API_BASE_URL}/stc-priorities`;

    async getByCompanyAndPriorityNo(companyNo: string, priorityNo: number): Promise<StcPriorityDto | null> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${priorityNo}`);
            if (!response.ok) {
                if (response.status === 404) {
                    return null;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error: any) {
            if (error.message?.includes('404')) {
                return null;
            }
            throw error;
        }
    }

    async getByCompanyNo(companyNo: string): Promise<StcPriorityDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/company/${companyNo}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getCurrent(): Promise<StcPriorityDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/current`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getAll(): Promise<StcPriorityDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async create(request: CreateStcPriorityRequest): Promise<StcPriorityDto> {
        const response = await AuthService.authenticatedPost(`${this.baseUrl}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async update(companyNo: string, priorityNo: number, request: UpdateStcPriorityRequest): Promise<StcPriorityDto> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/${companyNo}/${priorityNo}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async bulkUpdate(requests: BulkUpdateStcPriorityRequest[]): Promise<StcPriorityDto[]> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/bulk`, requests);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async delete(companyNo: string, priorityNo: number): Promise<void> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/${companyNo}/${priorityNo}`);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
    }

    async bulkDelete(ids: number[]): Promise<BulkDeleteResult> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/bulk`, ids);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async exists(companyNo: string, priorityNo: number): Promise<boolean> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${priorityNo}`);
            return response.ok;
        } catch (error: any) {
            return false;
        }
    }

    async search(companyNo: string, searchTerm: string): Promise<StcPriorityDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/search/${companyNo}?searchTerm=${encodeURIComponent(searchTerm)}`);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }
}

export const stcPriorityService = new StcPriorityService();
